/**
 * Values that must agree with the operator's provider app registrations.
 *
 * `APP_FOLDER_NAME` governs the folder Google Drive and WebDAV create. OneDrive
 * and Dropbox derive their app-folder name from the provider registration, so
 * those registrations must be named to match. Dropbox's app name is immutable
 * after creation — changing this constant means re-creating the Dropbox app.
 * See docs/ARCHITECTURE.md §12.1.
 */
export const APP_FOLDER_NAME = 'skysa-notes';

/** Name the app reports as its own in the marker file. */
export const APP_NAME = 'skysa-notes';

/** Marker file written at the root of the app folder on first connect. */
export const MARKER_FILE = '.notesapp.json';

/**
 * Layout version of the remote folder. A client that finds a marker with a
 * higher version than this opens the connection read-only — see `parseMarker`.
 */
export const MARKER_SCHEMA_VERSION = 1;

/** Storage providers this codebase knows. `dropbox`, `onedrive` and `gdrive`
 * have adapters; `webdav` is deferred indefinitely (docs/ARCHITECTURE.md §5.4). */
export const PROVIDER_KINDS = ['gdrive', 'onedrive', 'dropbox', 'webdav'] as const;

export type ProviderKind = (typeof PROVIDER_KINDS)[number];

/** Extension of a note file on every provider. */
export const NOTE_EXTENSION = '.md';

/** Anything at a path segment starting with this is invisible to the UI. */
export const HIDDEN_PREFIX = '.';

/**
 * The clipboard's folder at the root of the app folder (docs/ARCHITECTURE.md
 * §3, §7 "The clipboard"). Hidden, so the engine makes nothing of it and the
 * notebooks never show it.
 */
export const CLIPBOARD_FOLDER = '.clipboard';

/**
 * Where a source keeps the public half of its key for secrets, at the root of
 * the app folder (docs/ARCHITECTURE.md §3 "Secrets", §7 "Secrets in a note").
 * Hidden, so the engine makes nothing of it, and nothing secret is in it.
 */
export const ENCRYPTION_FILE = '.encryption.json';

/** The version of `.encryption.json` this build writes, and the newest it reads. */
export const ENCRYPTION_SCHEMA_VERSION = 1;

/** How many items the clipboard keeps; a paste past this lets go of the oldest. */
export const CLIPBOARD_ITEMS = 10;

/**
 * The scratchpad's folder at the root of the app folder (docs/ARCHITECTURE.md
 * §3, §7 "The scratchpad"). Hidden from the notebooks, but not from sync: the
 * notes in it are notes, which the scratchpad shows and nothing else does.
 */
export const SCRATCHPAD_FOLDER = '.scratchpad';

/**
 * The vault's passwords, one note each, at the root of the app folder
 * (docs/ARCHITECTURE.md §3, §7 "Passwords in the vault"). Hidden from the
 * notebooks, but not from sync, as the scratchpad's folder is.
 */
export const VAULT_FOLDER = '.vault';
